import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Send, MessageCircle } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [suggested, setSuggested] = useState([]);
  const endRef = useRef();

  useEffect(() => {
    (async () => {
      const [{ data: c }, { data: s }] = await Promise.all([
        api.get("/messages/conversations"),
        api.get("/users/suggested"),
      ]);
      setConvos(c);
      setSuggested(s);
      const toId = params.get("to");
      if (toId) {
        const u = c.find((cv) => cv.user.user_id === toId)?.user || s.find((su) => su.user_id === toId);
        if (u) openChat(u);
      }
    })();
  }, []);

  const openChat = async (u) => {
    setActiveUser(u);
    const { data } = await api.get(`/messages/${u.user_id}`);
    setMessages(data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeUser) return;
    const { data } = await api.post("/messages", { to_user_id: activeUser.user_id, text });
    setMessages((m) => [...m, data]);
    setText("");
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto py-8 px-4 md:px-8">
        <h1 className="font-heading font-bold text-4xl tracking-tight mb-6">Messages</h1>
        <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[70vh]">
          <aside className="surface rounded-xl p-3 overflow-y-auto">
            <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-2 px-2">Chats</div>
            {[...convos.map((c) => c.user), ...suggested.filter(s => !convos.find(c => c.user.user_id === s.user_id))].map((u) => (
              <button
                key={u.user_id}
                data-testid={`chat-with-${u.user_id}`}
                onClick={() => openChat(u)}
                className={`w-full flex items-center gap-3 p-2.5 rounded-md transition text-left ${
                  activeUser?.user_id === u.user_id ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                }`}
              >
                <img
                  src={u.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${u.user_id}`}
                  alt=""
                  className="w-9 h-9 rounded-md object-cover bg-white/5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{u.name}</div>
                  <div className="text-[11px] text-white/40 truncate font-mono">
                    {convos.find((c) => c.user.user_id === u.user_id)?.last_message || "say hi"}
                  </div>
                </div>
              </button>
            ))}
          </aside>

          <div className="surface rounded-xl flex flex-col overflow-hidden">
            {activeUser ? (
              <>
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/5">
                  <img
                    src={activeUser.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${activeUser.user_id}`}
                    alt=""
                    className="w-10 h-10 rounded-md object-cover bg-white/5"
                  />
                  <div>
                    <div className="font-heading font-semibold">{activeUser.name}</div>
                    <div className="text-[11px] text-white/40 font-mono">online</div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-2.5">
                  {messages.map((m) => {
                    const mine = m.from_id === user.user_id;
                    return (
                      <div
                        key={m.msg_id}
                        data-testid={`msg-${m.msg_id}`}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[70%] px-3.5 py-2 rounded-xl text-sm ${
                            mine
                              ? "cta-green rounded-br-sm font-medium"
                              : "surface-elev rounded-bl-sm"
                          }`}
                        >
                          <div>{m.text}</div>
                          <div className={`text-[10px] mt-0.5 font-mono ${mine ? "text-black/60" : "text-white/40"}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-white/5">
                  <input
                    data-testid="msg-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message…"
                    className="flex-1 bg-white/[0.04] border border-white/8 rounded-md px-4 py-2.5 focus:outline-none focus:border-[#00E676]/40 text-sm"
                  />
                  <button data-testid="msg-send" type="submit" className="cta-green rounded-md p-2.5">
                    <Send size={14} strokeWidth={2.5} />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-white/40">
                <MessageCircle size={40} strokeWidth={1.5} />
                <div className="mt-3 font-heading font-bold">Pick a chat to start</div>
                <div className="text-xs font-mono">Your DMs live here.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
